import { EventEmitter } from 'events';

import camelCase from 'lodash/camelCase';

import { maybeDecreaseEmitterMaxListeners, maybeIncreaseEmitterMaxListeners } from '../utils-utils';
import { type WindowEventEmitterEventNames, type WindowEventEmitterPropNames } from './types';

/**
 * Get window property value.
 * In D4, window value is retrieved using jQuery method because it ensures consistent value
 * and reducing the chance of having different returned value on different browser.
 * In D5, the use of jQuery is to be kept as minimum thus window value retrieval is no longer
 * using jQuery. However assuming there's a discrepency of returned value is found on
 * different browser, this will be one place to change them.
 *
 * @since ??
 *
 * @param {string} propName Property name.
 *
 * @returns {number}
 */
const getWindowProp = (propName: string): number => {
  switch (propName) {
    case 'height':
      return window.innerHeight;

    case 'top':
      return window.scrollY;

    case 'width':
      return window.innerWidth;

    default:
      return 0;
  }
};

/**
 * Class for emitting window events.
 *
 * Instead of directly adding event listener to real window events, the frontend script shoud add listener to events
 * emitted here. This becomes necessary because even though most Visual Builder mode has already consistent scroll
 * position in app window, some rendering such as layout block preview still has the scroll located on top window.
 * This emitter class also save attached listener callback into instance property which make listener callback removal
 * simpler.
 *
 * @since ??
 *
 * @augments EventEmitter Class that emits events.
 */
export class WindowEventEmitter extends EventEmitter {
  private height: number;

  private top: number;

  private width: number;

  registeredCallbacks: Record<string, (props: unknown) => unknown>;

  /**
   * Class constructor.
   *
   * @todo Right now this is a minified version of D4's `ETScriptWindowStore`. This refactoring
   * is inevitable because there are things in D4 that is no longer relevant in D5 such as
   * different scroll location in various mode, deprecating BFB, etc.
   *
   * @since ??
   */
  constructor() {
    super();

    this.height = getWindowProp('height');
    this.top = getWindowProp('top');
    this.width = getWindowProp('width');

    this.registeredCallbacks = {};

    // Attach class emitter instance to window events.
    this.attachToWindowEvents();
  }

  /**
   * Get property name's value.
   *
   * @since ??
   *
   * @param {WindowEventEmitterPropNames} propName Properity name.
   *
   * @returns {number}
   */
  getProp(propName: WindowEventEmitterPropNames): number {
    return this[propName];
  }

  /**
   * Set property name.
   *
   * @param {WindowEventEmitterPropNames} propName Class' property name.
   * @param {number} value Property name value.
   */
  setProp(propName: WindowEventEmitterPropNames, value: number): void {
    const prevValue = this[propName];

    this[propName] = value;

    this.emit(propName);

    // Only called `*.changed` event if the value is changed.
    if (prevValue !== value) {
      this.emit(`${propName}.changed`);
    }
  }

  /**
   * Add window event emitter listener.
   *
   * @since ??
   *
   * @param {WindowEventEmitterEventNames} eventName Property name that its emitted event you want to listen to.
   * @param {Function} callback Function callback.
   * @param {string} id Id for currently attached callback.
   */
  addWindowListener(eventName: WindowEventEmitterEventNames, callback: (...args: any[]) => void, id?: string): void {
    // If id is given, callback will be registered as instance property using given id as key
    // This will make multiple callback with the same name possible and make callback removal simpler.
    if (id) {
      // Prefix theid by the event name so the same id can be used on multiple events.
      const callbackName = `${camelCase(eventName)}${id}`;
      this.registeredCallbacks[callbackName] = callback;
      maybeIncreaseEmitterMaxListeners(this, eventName);
      this.on(eventName, this.registeredCallbacks[callbackName]);
    } else {
      maybeIncreaseEmitterMaxListeners(this, eventName);
      this.on(eventName, callback);
    }
  }

  /**
   * Remove window event emitter listener.
   *
   * @since ??
   *
   * @param {WindowEventEmitterEventNames} eventName Property name that its emitted event you want to listen to.
   * @param {Function} callback Function callback.
   * @param {string} id Id for currently attached callback.
   */
  removeWindowListener(eventName: WindowEventEmitterEventNames, callback: (...args: any[]) => void, id?: string): void {
    const callbackName = `${camelCase(eventName)}${id}`;

    // If id is given, the registered callback on instance property will be removed.
    if (id && Boolean(this.registeredCallbacks[callbackName])) {
      this.removeListener(eventName, this.registeredCallbacks[callbackName]);
      maybeDecreaseEmitterMaxListeners(this, eventName);

      delete this.registeredCallbacks[callbackName];
    } else {
      this.removeListener(eventName, callback);
      maybeDecreaseEmitterMaxListeners(this, eventName);
    }
  }

  /**
   * Register prop value change to window events.
   *
   * @since ??
   */
  attachToWindowEvents(): void {
    const setProp = this.setProp.bind(this) as typeof this.setProp;

    window.addEventListener('resize', () => {
      setProp('width', getWindowProp('width'));
      setProp('height', getWindowProp('height'));
    });

    window.addEventListener('scroll', () => {
      setProp('top', getWindowProp('top'));
    });
  }

  /**
   * Manually trigger an event with optional arguments.
   *
   * @param {WindowEventEmitterEventNames} eventName Event name to trigger.
   * @param {...any[]} args Arguments to pass to the event listeners.
   */
  trigger(eventName: WindowEventEmitterEventNames, ...args: any[]): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.emit(eventName, ...args);
  }
}

/**
 * Export instance of Window Event Emitter.
 *
 * Realistically this is what every component should import instead of importing WindowsEventEmitter on its own; this
 * ensures there's only one instance of WindowEventEmitter in one page.
 *
 * @since ??
 *
 * @constant
 * @returns {WindowEventEmitter} Instance of WindowEventEmitter.
 */
export const WindowEventEmitterInstance = new WindowEventEmitter();
